import shaka from 'shaka-player';
import isoLanguageConverter from 'iso-language-converter/index.es5';
import { KEY_SYSTEM_NAMES } from '@dtvgo/player-common-utils';

const { util: { Error: ShakaError }, net: { NetworkingEngine: { RequestType } } } = shaka;
const ERROR_NAMES_BY_CODE = invertEnum(ShakaError.Code);
const REQUEST_TYPE_NAMES = invertEnum(RequestType);
const DEFAULT_ERROR_CODES_TO_IGNORE_LIVE = /* @__PURE__ */ new Set([
  ShakaError.Code.BAD_HTTP_STATUS,
  ShakaError.Code.HTTP_ERROR,
  ShakaError.Code.TIMEOUT
]);
const DEFAULT_ERROR_CODES_TO_IGNORE_VOD = /* @__PURE__ */ new Set([]);
const DEFAULT_ERROR_CODES_TO_RELOAD = /* @__PURE__ */ new Set([
  ShakaError.Code.BUFFER_READ_OUT_OF_BOUNDS,
  ShakaError.Code.MEDIA_SOURCE_OPERATION_FAILED,
  ShakaError.Code.MEDIA_SOURCE_OPERATION_THREW,
  ShakaError.Code.VIDEO_ERROR,
  ShakaError.Code.DASH_INVALID_XML,
  ShakaError.Code.FAILED_TO_CREATE_CDM,
  ShakaError.Code.FAILED_TO_GENERATE_LICENSE_REQUEST,
  ShakaError.Code.LICENSE_RESPONSE_REJECTED,
  ShakaError.Code.EXPIRED
]);
const PROGRESS_UPDATE_INTERVAL = 500;
const DEFAULT_SECONDS_TO_REWIND = 30;
const BUFFERING_TIMEOUT_MS = 100;
const DELAYED_THRESHOLD_SECONDS = 5;
const TRY_TO_EXTRACT_FROM_INIT_DATA = "https://try.to.extract.from.init.data";
const EXTRACT_FROM_INIT_DATA = {
  [KEY_SYSTEM_NAMES.playready]: (initData) => {
    let playReadyObject = initData.initData;
    if (initData.type === "cenc") {
      playReadyObject = initData.initData.slice(32);
    }
    const playreadyData = playReadyObject.slice(10);
    const decodedPlayreadyData = new TextDecoder("utf-16").decode(playreadyData);
    return /<LA_URL>(.*)<\/LA_URL>/.exec(decodedPlayreadyData)?.[1];
  }
};
const MAP_KEY_SYSTEM_TO_PROP = invertEnum(KEY_SYSTEM_NAMES);
function getManifestUrlFromSource(source) {
  return source?.dash || source?.hls;
}
function hasSourceChanged(source, prevSource) {
  return getManifestUrlFromSource(source) !== getManifestUrlFromSource(prevSource);
}
function invertEnum(enumeration) {
  return Object.fromEntries(Object.entries(enumeration).map(([name, code]) => [code, name]));
}
function applyRequestModifications(request, modifiedRequest) {
  request.uris = modifiedRequest?.uris ?? request.uris;
  request.headers = {
    ...request.headers,
    ...modifiedRequest?.headers
  };
  request.body = modifiedRequest?.body ?? request.body;
  request.method = modifiedRequest?.method ?? request.method;
  request.allowCrossSiteCredentials = modifiedRequest?.allowCrossSiteCredentials ?? request.allowCrossSiteCredentials;
}
function findIndexOfFirstTrackOfLanguage(tracks, language) {
  return [...tracks].findIndex((track) => isoLanguageConverter(track.getAttribute("lang"), { to: 1 }) === language);
}
function keepSingleAudioTrack(period, language) {
  if (!language || !period)
    return;
  const audioTracks = period.querySelectorAll('AdaptationSet[mimeType="audio/mp4"]');
  if (audioTracks.length > 1) {
    let audioTrackIndex = findIndexOfFirstTrackOfLanguage(audioTracks, language);
    if (audioTrackIndex === -1) {
      audioTrackIndex = 0;
    }
    for (const [index, audioTrack] of audioTracks.entries()) {
      if (index !== audioTrackIndex) {
        audioTrack.remove();
      }
    }
  }
}
function makeSameLanguageAudioTracksDifferent(period) {
  if (!period)
    return;
  const langCounters = {};
  for (const audioTrack of period.querySelectorAll('AdaptationSet[mimeType="audio/mp4"]')) {
    const lang = audioTrack.getAttribute("lang");
    if (langCounters[lang]) {
      langCounters[lang] += 1;
    } else {
      langCounters[lang] = 1;
    }
    audioTrack.setAttribute("lang", `${lang}$${langCounters[lang]}`);
  }
}
function getTrackingURLFromManifest(manifestXML) {
  const locationURL = manifestXML?.querySelector("Location")?.textContent;
  if (!locationURL || !locationURL.includes("mediatailor"))
    return "";
  const { pathname, searchParams, origin } = new URL(locationURL);
  const arrayParams = pathname.split("/");
  const rootURL = "/v1/tracking";
  const hashedAccountId = arrayParams[3];
  const originId = arrayParams[4];
  const sessionId = searchParams.get("aws.sessionId");
  return `${origin}${rootURL}/${hashedAccountId}/${originId}/${sessionId}`;
}

export { BUFFERING_TIMEOUT_MS, DEFAULT_ERROR_CODES_TO_IGNORE_LIVE, DEFAULT_ERROR_CODES_TO_IGNORE_VOD, DEFAULT_ERROR_CODES_TO_RELOAD, DEFAULT_SECONDS_TO_REWIND, DELAYED_THRESHOLD_SECONDS, ERROR_NAMES_BY_CODE, EXTRACT_FROM_INIT_DATA, MAP_KEY_SYSTEM_TO_PROP, PROGRESS_UPDATE_INTERVAL, REQUEST_TYPE_NAMES, TRY_TO_EXTRACT_FROM_INIT_DATA, applyRequestModifications, findIndexOfFirstTrackOfLanguage, getManifestUrlFromSource, getTrackingURLFromManifest, hasSourceChanged, keepSingleAudioTrack, makeSameLanguageAudioTracksDifferent };
//# sourceMappingURL=utils.js.map
